/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: JackpotLevelValue.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  03.10.22 3:24 pm.
 * Last modified: 03.10.22 3:24 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useRef } from 'react';
import { formatCurrencyValue, getResizableParent, updateResizables } from '../../api/helpers';
import { useSettings } from '../../contexts/SettingsContext';
import store from '../../eventStore';
import { useCustomInView } from '../../hooks/useCustomInView';
import { useEffectWithRef } from '../../hooks/useEffectWithRef';
import { useWindowResize } from '../../hooks/useWindowResize';

const JackpotLevelValue = ({ jackpotName, denomination, level, className, defaultValue }: IJackpotLevelValueProps) => {
    const [jackpotRef, isVisible] = useCustomInView();
    const [setJackpotRefChild] = useEffectWithRef(node => {
        jackpotRef(getResizableParent(node as HTMLElement));
    });
    const [settings] = useSettings();
    const ref = useRef<HTMLSpanElement>(null);
    const intervalId = useRef<any>(null);

    const animateJackpotValue = () => {
        if (ref.current) {
            const value = store.JACKPOT_STATS_COUNTER.value;
            const currentValueText = ref.current.innerText;
            const jpObj = value?.get(jackpotName);
            let denomObj = jpObj?.denominations.get(denomination);
            if (!denomObj) {
                denomObj = jpObj?.denominations.get(jpObj.minDenomination);
            }
            const nextValueText = denomObj?.find(jp => jp.level === level)?.formattedValue || '';
            if (!nextValueText && defaultValue) {
                ref.current.innerText = formatCurrencyValue(defaultValue * denomination, settings.units);
                if (!currentValueText) {
                    updateResizables(getResizableParent(ref.current as HTMLElement));
                }
            } else if (currentValueText !== nextValueText) {
                ref.current.innerText = nextValueText;
                if (currentValueText.length !== nextValueText.length) {
                    updateResizables(getResizableParent(ref.current as HTMLElement));
                }
            }
            intervalId.current = requestAnimationFrame(animateJackpotValue);
        }
    };

    useWindowResize(() => {
        if (isVisible && ref.current) {
            // @ts-ignore
            requestAnimationFrame(() => {
                if (ref.current) {
                    updateResizables(getResizableParent(ref.current as HTMLElement));
                }
            });
        }
    }, [isVisible]);
    useEffect(() => {
        if (isVisible && !intervalId.current) {
            intervalId.current = requestAnimationFrame(animateJackpotValue);
        } else if (!isVisible && intervalId.current) {
            cancelAnimationFrame(intervalId.current);
            intervalId.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    return (
        <span ref={setJackpotRefChild}>
            <span className={className} ref={ref}></span>
        </span>
    );
};

interface IJackpotLevelValueProps {
    jackpotName: string;
    defaultValue?: number;
    denomination: number;
    level: number;
    className?: string;
}

export default JackpotLevelValue;
